import { pickScoringLabel } from "./scoring-i18n";
// Libellé lisible d'un code d'objectif (challenge / phase de parcours), selon le
// simulateur. Partagé entre le builder de parcours et la scène d'entraînement
// pour éviter la duplication du mapping.

type TFn = (key: string, opts?: Record<string, unknown>) => string;

// Objectifs « métier » par simulateur (clés i18n dans le namespace challenge).
const SIMULATOR_OBJECTIVES: Record<string, Array<{ value: string; labelKey: string }>> = {
  prospector: [{ value: "most_rdv", labelKey: "challenge.objectiveMostRdv" }],
  discovery: [{ value: "most_needs", labelKey: "challenge.objectiveMostNeeds" }],
  // Le simulateur s'appelle « Negotiator » en base (orthographe anglaise) : la
  // clé française ne correspondait à rien, et « Deal conclu » n'était jamais
  // proposé — ni au builder de parcours, ni au wizard de challenge. On accepte
  // les deux graphies, les environnements n'ayant pas tous le même libellé.
  negotiator: [{ value: "deal_closed", labelKey: "challenge.objectiveDealClosed" }],
  negociator: [{ value: "deal_closed", labelKey: "challenge.objectiveDealClosed" }],
  pitch: [{ value: "pitch_validated", labelKey: "challenge.objectivePitchValidated" }],
  satisfactor: [
    { value: "tension_mastered", labelKey: "challenge.objectiveTensionMastered" },
    { value: "clear_outcome", labelKey: "challenge.objectiveClearOutcome" },
  ],
};

/**
 * Renvoie le libellé d'un objectif (`code`) en fonction du simulateur :
 * objectif métier, critère de score du simulateur, ou objectif global.
 * Repli sur le code brut si introuvable.
 */
export function objectiveLabel(code: string | null | undefined, simulator: any, t: TFn, lang: "fr" | "en"): string {
  if (!code) return "";
  const simName = (simulator?.name || "").toLowerCase();
  const specific = (SIMULATOR_OBJECTIVES[simName] || []).map((o) => ({ value: o.value, label: t(o.labelKey) }));
  const scoring = (simulator?.scoringCriteria || [])
    .filter((c: any) => c.type === "score" && !["overall", "global", "score_total", "total_score"].includes(c.key))
    .map((c: any) => ({ value: c.key, label: pickScoringLabel(c, lang) || c.key }));
  const common = [
    { value: "cumulative_score", label: t("challenge.objectiveCumulativeScore") },
    { value: "best_average", label: t("challenge.objectiveBestAverage") },
    // Se valide à la PRATIQUE, pas à la note : le seul objectif possible sur un
    // simulateur qui ne produit aucune dimension notée (Prepa Meeting).
    { value: "sessions_done", label: t("challenge.objectiveSessionsDone") },
  ];
  return [...specific, ...scoring, ...common].find((o) => o.value === code)?.label || code;
}

// Classification (miroir du moteur progress.service.ts) pour l'explication.
const COUNT_OBJ = new Set(["most_rdv", "most_needs"]);
const SESSION_COUNT_OBJ = new Set(["sessions_done"]);
const BOOL_OBJ = new Set(["deal_closed", "pitch_validated", "tension_mastered", "clear_outcome"]);

/** Courte explication actionnable de l'objectif (comment l'atteindre). */
export function objectiveHint(code: string | null | undefined, target: number | null | undefined, t: TFn): string {
  if (!code) return "";
  if (BOOL_OBJ.has(code)) return t("objHint.boolean");
  if (target == null) return t("objHint.generic");
  if (SESSION_COUNT_OBJ.has(code)) return t("objHint.sessionsDone", { target });
  if (COUNT_OBJ.has(code)) return t("objHint.count", { target });
  if (code === "cumulative_score") return t("objHint.cumulative", { target });
  if (code === "best_average") return t("objHint.average", { target });
  return t("objHint.criterion", { target });
}
